import { useAdminDB } from '@/composables/useAdminDB'

// Estado de módulo (singleton) — carregado uma única vez, reaproveitado por
// todas as telas que buscam conversas (Dashboard, Mobile, Resumo, SLA, SLA
// Telão). Aceita uma pequena janela de corrida no primeiríssimo carregamento
// da aba (o Set ainda vazio até o loadBlacklist() resolver) — bloquear a
// dashboard nisso não vale a pena pra uma lista que muda raramente.
const blacklistedIds = new Set<number>()

useAdminDB().loadBlacklist()
  .then(rows => rows.forEach(r => blacklistedIds.add(r.contact_id)))
  .catch(() => { /* mantém o Set vazio; próximas detecções recadastram normalmente */ })

const BLACKLIST_LABEL = 'lista-negra'

/** Mesmo estilo de normalização usado em `ForaDeHorarioTab.vue` (isForaLabel). */
export function isBlacklistLabel(lbl: string): boolean {
  return lbl.toLowerCase().replace(/[\s_]+/g, '-') === BLACKLIST_LABEL
}

interface BlacklistableConversation {
  contact: { id: number; name: string; phone: string }
  labels: string[]
  team?: { name: string } | null
}

/**
 * Filtra conversas de contatos na lista negra — e detecta/registra automaticamente
 * quem ainda não estava (etiqueta `lista_negra` numa conversa). Chamada pelas 5
 * telas que buscam conversas, logo após o parse de cada uma, antes de qualquer
 * notificação/exibição. Persistir é necessário porque uma conversa NOVA do mesmo
 * contato não carrega a etiqueta de novo — sem isso, o contato "voltaria a aparecer"
 * a cada novo contato.
 */
export function filterOutBlacklisted<T extends BlacklistableConversation>(conversations: T[]): T[] {
  return conversations.filter(c => {
    const hasLabel = c.labels.some(isBlacklistLabel)
    if (hasLabel && !blacklistedIds.has(c.contact.id)) {
      blacklistedIds.add(c.contact.id) // filtra já nesta mesma passada
      useAdminDB().addToBlacklist({
        contact_id:   c.contact.id,
        name:         c.contact.name,
        phone:        c.contact.phone,
        email:        '',
        reason_label: 'lista_negra',
        context_team: c.team?.name ?? '',
      }).catch(err => console.warn('[Blacklist] addToBlacklist:', err))
    }
    return !hasLabel && !blacklistedIds.has(c.contact.id)
  })
}

/** Usado pela tela Admin → Black List ao remover um contato, pra parar de filtrar imediatamente. */
export function forgetBlacklisted(contactId: number): void {
  blacklistedIds.delete(contactId)
}
